import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState } from "typedefs";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import { cls } from "app/utils";
import { Loading } from "app/components/Wrappers";
import useAcademySetupHint from "app/utils/hooks/useAcademySetupHint";
import useAcademySetupItems from "app/utils/hooks/useAcademySetupItems";
import useActions from "app/utils/hooks/useActions";
import VideoAnalysisModal from "app/components/GettingStarted/VideoAnalysisModal";
import StripeRequiredModal from "../StripeRequiredModal";
import { gettingStartedLinks } from "app/components/GettingStarted/links";
import { markOptionalItem, markSetupCta } from "app/actions/academySetup";
import {
  trackAppDownloadCtaClicked,
  trackChecklistItemClicked,
  trackChecklistItemSkipped,
} from "app/utils/analytics-helpers";
import bullseyeIcon from "app/images/bullseye-arrow-orange.svg";
import downChevron from "app/images/down-chevron.svg";
import completedCheck from "app/images/blue-check-icon.svg";
// The same transparent device composition the completion modal and the
// wizard Welcome hero render, which is what Figma 408:12739 shows.
import heroImage from "app/images/academy-wizard-hero.webp";
import {
  AcademySetupItem,
  academySetupPhases,
  allComplete,
  completedCount,
} from "../items";
import styles from "./styles.module.scss";

interface SetupChecklistProps {
  /**
   * The feed/home banner: hides once complete and opens collapsed on every
   * visit (the toggle is not remembered). The /academy/setup page always
   * opens expanded.
   */
  banner?: boolean;
}

const SetupChecklist = ({ banner = false }: SetupChecklistProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const { items, loaded, failed } = useAcademySetupItems();
  const actions = useActions({ markOptionalItem, markSetupCta });
  const [collapsed, setCollapsed] = useState<boolean>(banner);
  const appHint = useAcademySetupHint("mobile_app");
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [stripeRequiredOpen, setStripeRequiredOpen] = useState(false);

  if (!isAcademyOnboardingCoach(self)) {
    return null;
  }

  if (!loaded) {
    // The feed/home banner stays hidden until completion arrives; the
    // /academy/setup page shows a spinner in the checklist's place. A failed
    // fetch has no designed state, so the page shows its other sections and
    // the next mount retries rather than spinning indefinitely.
    if (banner || failed) {
      return null;
    }
    return <Loading isLoading={true} loadType="spinner" />;
  }

  const complete = allComplete(items);

  if (banner && complete) {
    return null;
  }

  const completed = completedCount(items);
  const total = items.length;

  const toggleCollapsed = () => {
    setCollapsed((current) => !current);
  };

  // 1-based position across the whole list (spec v2 §6 item_position).
  const positionOf = (item: AcademySetupItem) => items.indexOf(item) + 1;

  const closeAppModal = () => {
    setAppModalOpen(false);
    if (appHint.active) {
      appHint.dismiss();
    }
  };

  const itemByKey = (key: AcademySetupItem["key"]) =>
    items.find((item) => item.key === key);

  const openItem = (item: AcademySetupItem) => {
    actions.markSetupCta(item.key, "checklist_cta");
    // Optional items count as complete once the coach has opened their page,
    // whether or not they act there — the row keeps saying "Not set up yet".
    if (item.optional && !item.complete) {
      actions.markOptionalItem(item.key, "visited");
    }
    // The coachmark says how to finish the item, so a row the API already
    // counts as done (Edit on a complete facility profile) opens its page plain.
    history.push(
      `${item.path}${item.search || ""}`,
      item.done ? undefined : { academySetupHint: item.key },
    );
  };

  const onItemAction = (item: AcademySetupItem) => {
    trackChecklistItemClicked(item.key, item.phase, positionOf(item));
    if (item.key === "mobile_app") {
      // The click never completes the item; only a mobile session does.
      actions.markSetupCta(item.key, "checklist_cta");
      trackAppDownloadCtaClicked(completed);
      setAppModalOpen(true);
      return;
    }
    // Packages can't switch to online payment later, so an unconnected
    // Stripe gets a warning first (Figma 464:6650).
    if (
      item.key === "packages_subscriptions" &&
      !item.done &&
      !itemByKey("stripe_connect")?.done
    ) {
      setStripeRequiredOpen(true);
      return;
    }
    openItem(item);
  };

  const proceedToPackages = () => {
    setStripeRequiredOpen(false);
    const packages = itemByKey("packages_subscriptions");
    if (packages) {
      openItem(packages);
    }
  };

  const setUpStripe = () => {
    setStripeRequiredOpen(false);
    const stripe = itemByKey("stripe_connect");
    if (stripe) {
      openItem(stripe);
    }
  };

  const onItemSkip = (item: AcademySetupItem) => {
    trackChecklistItemSkipped(item.key, item.phase, positionOf(item));
    // The skip completes the row for progress, so record the checklist as
    // its source first; otherwise the completion reads as organic_nav.
    actions.markSetupCta(item.key, "checklist_cta");
    actions.markOptionalItem(item.key, "skipped");
  };

  const actionLabel = (item: AcademySetupItem): string => {
    // "Created" only once the API flag is true — a visited/skipped row keeps
    // its imperative label because the page hasn't actually been created.
    if (item.done && item.completeActionKey) {
      return t(item.completeActionKey);
    }
    return t(item.actionKey);
  };

  const renderItem = (item: AcademySetupItem) => (
    <li key={item.key} className={styles.step}>
      {item.complete ? (
        <img
          className={styles.check}
          src={completedCheck}
          alt={t("Completed")}
        />
      ) : (
        <span className={styles.check_placeholder} />
      )}
      <div className={styles.step_text}>
        <p
          className={cls(styles.step_title, {
            [styles.step_title_done]: item.done,
          })}
        >
          {t(item.titleKey)}
        </p>
        {!item.complete && item.subtitleKey && (
          <p className={styles.step_subtitle}>
            {t(item.subtitleKey)}
            {item.optional && (
              <>
                {" "}
                <button
                  type="button"
                  className={styles.skip_link}
                  onClick={() => onItemSkip(item)}
                >
                  {t("Skip for now")}
                </button>
              </>
            )}
          </p>
        )}
        {item.complete && !item.done && (
          // Visited or skipped: checked for progress, but the thing itself
          // isn't set up yet, so say so and keep the action live. Only an
          // explicit skip reads as skipped — a visit made no such choice.
          <p className={styles.step_subtitle}>
            {item.optionalState === "skipped"
              ? t("Skipped for now")
              : t("Not set up yet")}
          </p>
        )}
      </div>
      <button
        type="button"
        className={styles.action_pill}
        // Done rows are status, not actions — unless the design keeps the
        // row editable (facility_profile's "Edit").
        disabled={item.done && !item.staysActionable}
        onClick={() => onItemAction(item)}
      >
        {actionLabel(item)}
      </button>
    </li>
  );

  const header = (
    <>
      <div className={styles.header}>
        <img className={styles.bullseye} src={bullseyeIcon} alt="" />
        <h2 className={styles.title}>{t("Finish setup")}</h2>
        <p className={styles.summary}>
          {/* Figma 408:12728: the party popper only joins the 9 of 9 state. */}
          {complete ? "🎉 " : null}
          {t("{{completed}} of {{total}} complete", { completed, total })}
        </p>
        {/* The 9 of 9 card has nothing to collapse, and the design drops the chevron. */}
        {!complete && (
          <button
            type="button"
            className={styles.collapse_button}
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t("Expand") : t("Collapse")}
          >
            <img
              className={cls(styles.chevron, {
                [styles.chevron_collapsed]: collapsed,
              })}
              src={downChevron}
              alt=""
            />
          </button>
        )}
      </div>
      <div className={styles.progress_track}>
        <div
          className={styles.progress_fill}
          style={{ width: `${(completed / total) * 100}%` }}
        />
      </div>
    </>
  );

  if (complete) {
    // Figma 408:12668 "You're all set up": the list gives way to the
    // celebration once every item is complete.
    return (
      <section className={styles.container} data-testid="academy-setup-complete">
        {header}
        <div className={styles.complete_image_panel}>
          <img className={styles.complete_hero} src={heroImage} alt="" />
        </div>
        <div className={styles.complete_texts}>
          <h3 className={styles.complete_title}>{t("You're all set up")}</h3>
          <p className={styles.complete_body}>
            {t(
              "Your academy is ready to accept bookings and manage athletes. Time to put it to work.",
            )}
          </p>
        </div>
        <button
          type="button"
          className={styles.complete_cta}
          onClick={() => history.push("/")}
        >
          {t("Go to Home")}
        </button>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      {header}
      {!collapsed &&
        academySetupPhases.map((phase, index) => (
          <div key={phase.key} className={styles.phase}>
            <div className={styles.phase_header}>
              <span className={styles.phase_badge}>
                {t("Phase {{number}}", { number: index + 1 })}
              </span>
              <h3 className={styles.phase_title}>{t(phase.titleKey)}</h3>
            </div>
            <ul className={styles.steps}>
              {items
                .filter((item) => item.phase === phase.key)
                .map(renderItem)}
            </ul>
          </div>
        ))}
      <VideoAnalysisModal
        url={gettingStartedLinks}
        isOpen={appModalOpen || appHint.active}
        onClose={closeAppModal}
      />
      <StripeRequiredModal
        isOpen={stripeRequiredOpen}
        onClose={() => setStripeRequiredOpen(false)}
        onProceed={proceedToPackages}
        onSetUpStripe={setUpStripe}
      />
    </section>
  );
};

export default SetupChecklist;
